import { useMemo } from "react";
import { Box, LinearProgress, Typography, Chip, Stack, BoxProps, Button } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import { FileProgressStage, UploadProgressBarsProps } from "../../types";
import { formatFileSize } from "../../utilities/fileUtils";

// Display threshold for UI purposes only - actual threshold comes from backend
const getDisplayMultipartThreshold = () => 5 * 1024 * 1024; // 5MB

type ProgressContainerProps = BoxProps;
type ProgressContainerSlotProps = BoxProps;

function ProgressContainer({ sx, ...rest }: ProgressContainerProps) {
  return (
    <Box
      sx={{
        display: "flex",
        background: "#fff",
        padding: "0.75rem",
        flexDirection: "column",
        gap: "0.5rem",
        borderRadius: "0.5rem",
        border: "1px solid",
        borderColor: "grey.200",
        ...sx
      }}
      {...rest}
    />
  );
}

function Header({ sx, ...rest }: ProgressContainerSlotProps) {
  return <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1, ...sx }} {...rest} />;
}

function Content({ sx, ...rest }: ProgressContainerSlotProps) {
  return <Box sx={{ display: "flex", flexDirection: "row", gap: "0.5rem", alignItems: "center", ...sx }} {...rest} />;
}

function Footer({ sx, ...rest }: ProgressContainerSlotProps) {
  return <Box sx={{ display: "flex", justifyContent: "space-between", ...sx }} {...rest} />;
}

export const UploadProgress = Object.assign(ProgressContainer, {
  Header,
  Content,
  Footer
});

export default function UploadProgressBars({ progress, items, onComplete, onFailure, onCancel, isFinish, isFailed }: UploadProgressBarsProps) {
  const activeItems = items.filter((item) => progress[item.id]);

  // Calculate total progress across ALL items
  const totalProgress = useMemo(() => {
    // First, calculate the total size of files that have a File object
    const totalSize = items
      .filter((item) => item.file) // Only include items with files
      .reduce((acc, item) => acc + item.file!.size, 0); // Safe to use ! after filter

    // Count completed files
    const completedFiles = items.filter((item) => {
      const itemProgress = progress[item.id];
      const isCompleted = itemProgress && itemProgress.progress >= 100;
      return isCompleted;
    }).length;

    // if total upload size is zero fall back to
    // file counts so progress bar shows how many file
    // has been upload successfully
    if (totalSize === 0) {
      const totalItems = items?.length;
      return {
        percentage: Math.round((completedFiles / totalItems) * 100),
        completed: 0,
        total: totalItems || 0,
        completedFiles: completedFiles,
        progressType: "fileCount"
      };
    }

    // Calculate completed bytes across all items using item IDs
    const completedBytes = items
      .filter((item) => item.file && progress[item.id]) // Match by item.id
      .reduce((acc, item) => {
        const itemProgress = progress[item.id];
        const completed = itemProgress?.completed || 0;
        return acc + completed;
      }, 0);

    const result = {
      percentage: Math.round((completedBytes / totalSize) * 100),
      completed: completedBytes,
      total: totalSize,
      completedFiles: completedFiles,
      progressType: "fileSize"
    };

    return result;
  }, [progress, items]);

  // Split items by upload method
  const { multipartItems, standardItems } = useMemo(() => {
    const multipart: typeof items = [];
    const standard: typeof items = [];

    items.forEach((item) => {
      if (item.file && item.file.size > getDisplayMultipartThreshold()) {
        multipart.push(item);
      } else {
        standard.push(item);
      }
    });

    return { multipartItems: multipart, standardItems: standard };
  }, [items]);

  // Update stages to match our S3 upload flow
  const stageLabels: Record<FileProgressStage, string> = {
    pending: "Pending",
    hashing: "Calculating Hash",
    presigning: "Getting Upload URL",
    uploading: "Uploading to S3",
    processing: "Server Processing",
    server: "Server Processing",
    completed: "Upload Completed",
    failed: "Upload Failed"
  };

  const stageColors: Record<FileProgressStage, string> = {
    pending: "#9e9e9e", // grey
    hashing: "#ff9800", // orange
    presigning: "#2196f3", // blue
    uploading: "#4caf50", // green
    processing: "#9c27b0", // purple
    server: "#9c27b0", // same as processing
    completed: "#4caf50", // green
    failed: "#f44336" // red
  };

  const getUploadMethodChip = (fileSize: number) => {
    const isMultipart = fileSize > getDisplayMultipartThreshold();
    return (
      <Chip
        label={isMultipart ? "Multipart" : "Standard"}
        size='small'
        variant='outlined'
        sx={{
          padding: "0.25rem 0.5rem",
          minWidth: "auto",
          backgroundColor: isMultipart ? "#e3f2fd" : "#f3e5f5",
          borderColor: isMultipart ? "#2196f3" : "#9c27b0",
          color: isMultipart ? "#1976d2" : "#7b1fa2",
          fontWeight: "bold",
          fontSize: "0.625rem",
          "& .MuiChip-label": {
            paddingInline: 0
          }
        }}
      />
    );
  };

  return (
    <Box
      className='upload-progressbar-container'
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "1rem",
        padding: "1.5rem 0 1.5rem 1rem",
        backgroundColor: "white"
      }}
    >
      <Stack gap={"0.75rem"}>
        <Typography variant='h2' fontWeight={600} color='grey.800'>
          Uploading Status
        </Typography>
        <Typography color='grey.600'>
          {totalProgress.completedFiles} of {items.length} uploaded
        </Typography>
      </Stack>

      {/* Total Progress Section */}
      <Box paddingRight={"1.5rem"}>
        <UploadProgress sx={{ gap: "0.75rem" }}>
          <UploadProgress.Header>
            <Typography variant='h6' color='textPrimary'>
              Overall Progress
            </Typography>
          </UploadProgress.Header>

          <UploadProgress.Content>
            <LinearProgress
              variant='determinate'
              value={totalProgress.percentage}
              sx={{
                flex: 1,
                height: "0.3125rem",
                borderRadius: 3,
                backgroundColor: "#E6E6E6",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: "#4caf50"
                }
              }}
            />
            <Typography variant='body2' color='textPrimary'>
              {totalProgress.percentage}%
            </Typography>
          </UploadProgress.Content>

          <UploadProgress.Footer sx={{ justifyContent: "flex-start", alignItems: "center", gap: "1rem" }}>
            <Typography variant='body2' color='grey.600'>
              {totalProgress.completedFiles} of {items.length} files uploaded <Typography component='span'>• </Typography>
              {totalProgress?.progressType === "fileSize" &&
                `${(totalProgress.completed / (1024 * 1024)).toFixed(1)} MB / 
     ${(totalProgress.total / (1024 * 1024)).toFixed(1)} MB`}
            </Typography>
            <Typography variant='body2' color='textSecondary'>
              {standardItems.length > 0 && `${standardItems.length} standard`}
              {standardItems.length > 0 && multipartItems.length > 0 && " • "}
              {multipartItems.length > 0 && `${multipartItems.length} multipart`}
            </Typography>
          </UploadProgress.Footer>
        </UploadProgress>
      </Box>
      {/* Individual Progress Bars */}
      <Box
        className='scroller upload-proress-items'
        sx={{
          flex: 1,
          overflow: "auto",
          maxHeight: "50dvh",
          display: "flex",
          flexDirection: "column",
          gap: "1rem",
          paddingRight: "1rem"
        }}
      >
        {activeItems.map((item) => {
          const fileProgress = progress[item.id];
          if (!fileProgress) return null;

          const progressValue = Math.min(Math.max(fileProgress.progress, 0), 100);
          const isCompleted = progressValue === 100 && fileProgress.stage !== "failed" && fileProgress.stage !== "pending";
          const isFailed = fileProgress.stage === "failed";

          const stageLabel = isFailed ? "Upload Failed" : isCompleted ? "Upload Completed" : stageLabels[fileProgress.stage];

          const fileSize = item.file?.size || 0;

          return (
            <UploadProgress key={item.id} sx={{ position: "relative" }}>
              <UploadProgress.Header>
                <Stack
                  sx={{
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "0.5rem",
                    flex: 1,
                    overflow: "hidden"
                  }}
                >
                  <InsertDriveFileOutlinedIcon />
                  <Typography variant='h6' component='span' color='grey.800' overflow='hidden' textOverflow='ellipsis' whiteSpace='nowrap'>
                    {item.fileName || item.name}
                  </Typography>
                </Stack>

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "0.5rem" }}>
                  {getUploadMethodChip(fileSize)}
                  <Typography variant='body1' color='textSecondary'>
                    {stageLabel}
                  </Typography>
                </Box>
              </UploadProgress.Header>

              {/* Progress bar*/}
              <UploadProgress.Content>
                <LinearProgress
                  variant='determinate'
                  value={progressValue}
                  sx={{
                    flex: 1,
                    height: "0.3125rem",
                    borderRadius: 2,
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    "& .MuiLinearProgress-bar": {
                      transition: "transform 0.2s linear",
                      backgroundColor: isFailed ? stageColors.failed : isCompleted ? stageColors.completed : stageColors[fileProgress.stage]
                    }
                  }}
                />
                <Typography variant='body2' color={isFailed ? "error.main" : isCompleted ? "success.main" : "grey.600"}>
                  ({Math.round(progressValue)}%)
                </Typography>
              </UploadProgress.Content>

              {/* Progress footer*/}
              <UploadProgress.Footer>
                <Typography variant='body2' color={isFailed ? "error.main" : "textSecondary"}>
                  {fileProgress.completed >= 0 && `${formatFileSize(fileProgress.completed) || 0} / `}
                  {formatFileSize(fileSize)}
                </Typography>
              </UploadProgress.Footer>

              {fileProgress?.stage === "pending" && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: "linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.9) 45%, transparent 60%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.2s linear infinite",
                    zIndex: 2,
                    pointerEvents: "none",
                    "@keyframes shimmer": {
                      to: { backgroundPositionX: "-200%" }
                    }
                  }}
                />
              )}
            </UploadProgress>
          );
        })}
      </Box>

      {(onComplete || onCancel) && (
        <Box sx={{ display: "flex", justifyContent: "right", gap: "1rem" }}>
          {/* Cancel stays available while the batch is still uploading. */}
          {onCancel && !isFinish && (
            <Button onClick={onCancel} variant='secondary' disableElevation>
              Cancel Upload
            </Button>
          )}
          {isFailed && onFailure && (
            <Button onClick={onFailure} variant='secondary' disableElevation>
              Close
            </Button>
          )}
          {onComplete && (
            <Button onClick={onComplete} disabled={totalProgress?.percentage < 100 || !isFinish} variant='contained' disableElevation>
              Continue
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}
